import { useEffect, useState } from 'react';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
import { type AvailabilityRuleSchema } from '@/lib/schemas/tutor-profile';
import type { ChildResponse } from '@/lib/schemas/parent-profile';
import type { z } from 'zod';
import { Edit2 } from 'lucide-react';
import type { RecurrenceRule } from '@/lib/schemas/booking';
import { generateRecurrencePreview, getRecurrenceCountEstimate } from '@/lib/recurrence';
import { createBooking, createRecurringSeries } from '@/lib/booking-api';

type AvailabilityRule = z.infer<typeof AvailabilityRuleSchema>;

interface SlotOption {
  startUtc: string;
  endUtc: string;
  displayTime: string;
  hour: number;
  dateStr: string;
}

const TIMEZONES = [
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'America/Toronto',
  'America/Mexico_City', 'America/Bogota', 'America/Lima', 'America/São_Paulo', 'America/Buenos_Aires',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Amsterdam', 'Europe/Brussels',
  'Europe/Vienna', 'Europe/Prague', 'Europe/Moscow', 'Europe/Istanbul', 'Europe/Athens',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Tokyo',
  'Asia/Seoul', 'Asia/Singapore', 'Asia/Manila', 'Asia/Jakarta', 'Asia/Karachi', 'Asia/Kabul',
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Perth', 'Australia/Adelaide',
  'Pacific/Auckland', 'Pacific/Fiji',
  'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Casablanca',
  'UTC',
].sort();

const WEEKDAY_TO_KEY: Record<string, AvailabilityRule['day']> = {
  Sunday: 'sun', Monday: 'mon', Tuesday: 'tue', Wednesday: 'wed', Thursday: 'thu', Friday: 'fri', Saturday: 'sat',
};

interface SlotPickerModalProps {
  open: boolean;
  onClose: () => void;
  tutorId: string;
  tutorName: string;
  homeTz: string;
  availability: AvailabilityRule[];
  children: ChildResponse[];
  accessToken: string;
  onBookingComplete?: () => void;
}

function getUserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

function zonedToUtc(year: number, month: number, day: number, hours: number, minutes: number, tz: string): Date {
  const targetMs = Date.UTC(year, month - 1, day, hours, minutes);
  const guess = new Date(targetMs);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false,
  }).formatToParts(guess);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  const displayedMs = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
  return new Date(targetMs + (targetMs - displayedMs));
}

function format12Hour(hour: number): string {
  if (hour === 0) return '12:00 AM';
  if (hour < 12) return `${hour}:00 AM`;
  if (hour === 12) return '12:00 PM';
  return `${hour - 12}:00 PM`;
}

function buildSlots(availability: AvailabilityRule[], tutorTz: string, parentTz: string, lookaheadDays = 60): SlotOption[] {
  const slots: SlotOption[] = [];
  const now = new Date();

  for (let i = 1; i <= lookaheadDays; i++) {
    const date = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tutorTz, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'long',
    }).formatToParts(date);
    const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
    const dayKey = WEEKDAY_TO_KEY[weekday];
    const year = Number(parts.find((p) => p.type === 'year')?.value);
    const month = Number(parts.find((p) => p.type === 'month')?.value);
    const day = Number(parts.find((p) => p.type === 'day')?.value);

    for (const rule of availability) {
      if (rule.day !== dayKey) continue;
      const [startH = 0, startM = 0] = rule.startLocal.split(':').map(Number);
      const [endH = 0] = rule.endLocal.split(':').map(Number);

      for (let h = startH; h < endH; h++) {
        const slotStartUtc = zonedToUtc(year, month, day, h, startM, tutorTz);
        const slotEndUtc = zonedToUtc(year, month, day, h + 1, startM, tutorTz);
        if (slotStartUtc <= now) continue;

        const displayParts = new Intl.DateTimeFormat('en-US', {
          timeZone: parentTz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
        }).formatToParts(slotStartUtc);

        const displayYear = displayParts.find((p) => p.type === 'year')?.value ?? '2000';
        const displayMonth = displayParts.find((p) => p.type === 'month')?.value ?? '01';
        const displayDay = displayParts.find((p) => p.type === 'day')?.value ?? '01';
        const displayHour = Number(displayParts.find((p) => p.type === 'hour')?.value ?? 0);
        const dateStr = `${displayYear}-${displayMonth}-${displayDay}`;
        const displayTime = format12Hour(displayHour);

        slots.push({ startUtc: slotStartUtc.toISOString(), endUtc: slotEndUtc.toISOString(), displayTime, hour: displayHour, dateStr });
      }
    }
  }

  return slots;
}

export function SlotPickerModal({
  open, onClose, tutorId, tutorName, homeTz, availability, children, accessToken, onBookingComplete,
}: SlotPickerModalProps) {
  const [selectedChild, setSelectedChild] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<SlotOption | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parentTz, setParentTz] = useState<string>(() => getUserTimeZone());
  const [editingTz, setEditingTz] = useState(false);
  const [tzInput, setTzInput] = useState(parentTz);
  const [recurrenceType, setRecurrenceType] = useState<'one-time' | 'recurring'>('one-time');
  const [recurRule, setRecurRule] = useState<RecurrenceRule>({
    frequency: 'weekly',
    seriesIndex: 0,
  });
  const [subject, setSubject] = useState('');
  const [classTitle, setClassTitle] = useState(`Class with ${tutorName}`);

  useEffect(() => {
    if (open) {
      setSelectedTime(null);
      setSelectedDate(null);
      setError(null);
      setEditingTz(false);
      const userTz = getUserTimeZone();
      setParentTz(userTz);
      setTzInput(userTz);
      if (children.length === 1) {
        setSelectedChild(children[0]!.id);
      } else {
        setSelectedChild('');
      }
      setRecurrenceType('one-time');
      setClassTitle(`Class with ${tutorName}`);
    }
  }, [open, children, tutorName]);

  const slots = buildSlots(availability, homeTz, parentTz);
  const uniqueDates = Array.from(new Set(slots.map((s) => s.dateStr))).sort();
  const availableTimesForDate = selectedDate ? slots.filter((s) => s.dateStr === selectedDate) : slots;
  const morningTimes = availableTimesForDate.filter((s) => s.hour >= 5 && s.hour < 12).reduce((acc, slot) => {
    if (!acc.find((s) => s.hour === slot.hour)) acc.push(slot);
    return acc;
  }, [] as SlotOption[]).sort((a, b) => a.hour - b.hour);
  const eveningTimes = availableTimesForDate.filter((s) => s.hour >= 12).reduce((acc, slot) => {
    if (!acc.find((s) => s.hour === slot.hour)) acc.push(slot);
    return acc;
  }, [] as SlotOption[]).sort((a, b) => a.hour - b.hour);

  const handleConfirm = async () => {
    setError(null);
    if (!selectedChild) { setError('Please select a child.'); return; }
    if (!selectedTime) { setError('Please select a time slot.'); return; }
    if (!selectedDate) { setError('Please select a date.'); return; }

    const exactSlot = slots.find((s) => s.dateStr === selectedDate && s.hour === selectedTime.hour);
    if (!exactSlot) { setError('Selected time is not available for the chosen date.'); return; }

    setSubmitting(true);
    try {
      if (recurrenceType === 'one-time') {
        await createBooking(accessToken, {
          tutorId,
          childId: selectedChild,
          slotStartUtc: exactSlot.startUtc,
          slotEndUtc: exactSlot.endUtc,
          type: 'demo',
          tutorTimeZone: homeTz,
          parentTimeZone: parentTz,
          classTitle: classTitle || undefined,
          subject: subject || undefined,
        });
      } else {
        await createRecurringSeries(accessToken, {
          tutorId,
          childId: selectedChild,
          slotStartUtc: exactSlot.startUtc,
          slotEndUtc: exactSlot.endUtc,
          type: 'demo',
          tutorTimeZone: homeTz,
          parentTimeZone: parentTz,
          recurrenceRule: recurRule,
          classTitle: classTitle || undefined,
          subject: subject || undefined,
        });
      }
      onBookingComplete?.();
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to create booking';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const twoMonths = [
    { year: currentYear, month: currentMonth },
    { year: currentMonth === 12 ? currentYear + 1 : currentYear, month: currentMonth === 12 ? 1 : currentMonth + 1 },
  ];

  const datesByMonth = uniqueDates.reduce((acc, dateStr) => {
    const [year, month] = dateStr.split('-');
    const key = `${year}-${month}`;
    if (!acc[key]) acc[key] = [];
    acc[key]!.push(dateStr);
    return acc;
  }, {} as Record<string, string[]>);

  const formatMonthYear = (year: number, month: number): string => new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const getFirstDayOfMonth = (year: number, month: number): number => new Date(year, month - 1, 1).getDay();
  const getDaysInMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();
  const getYearMonthKey = (year: number, month: number): string => `${year}-${String(month).padStart(2, '0')}`;

  const classCount = recurrenceType === 'one-time' ? 1 : getRecurrenceCountEstimate(recurRule);
  const recurrencePreview = recurrenceType === 'one-time' ? '' : generateRecurrencePreview(recurRule, recurRule.until);

  return (
    <Dialog open={open} onClose={onClose} title={`Book a class with ${tutorName}`} maxWidth="max-w-6xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT PANEL: Event Details */}
        <div className="space-y-4">
          {/* Class Title */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Class Title</label>
            <input
              type="text"
              value={classTitle}
              onChange={(e) => setClassTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Subject (Optional)</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g., Math, Science"
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
          </div>

          {/* Child */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Child <span className="text-red-500">*</span></label>
            <select
              value={selectedChild}
              onChange={(e) => setSelectedChild(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            >
              <option value="">Choose a child...</option>
              {children.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.age} yrs)</option>)}
            </select>
          </div>

          {/* Recurrence */}
          <div className="border-t pt-4">
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400">Booking Type</label>
              <div className="flex gap-2">
                {(['one-time', 'recurring'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setRecurrenceType(type)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      recurrenceType === type
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {type === 'one-time' ? 'One-Time' : 'Recurring'}
                  </button>
                ))}
              </div>
            </div>

            {recurrenceType === 'recurring' && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Frequency</label>
                  <select
                    value={recurRule.frequency}
                    onChange={(e) => setRecurRule({ ...recurRule, frequency: e.target.value as RecurrenceRule['frequency'] })}
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white mt-1"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom">Custom (Specific Days)</option>
                  </select>
                </div>

                {['weekly', 'biweekly', 'custom'].includes(recurRule.frequency) && (
                  <div>
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Days</label>
                    <div className="grid grid-cols-4 gap-1 mt-1">
                      {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((day) => (
                        <button
                          key={day}
                          onClick={() => {
                            const days = recurRule.days || [];
                            setRecurRule({
                              ...recurRule,
                              days: days.includes(day) ? days.filter((d) => d !== day) : [...days, day],
                            });
                          }}
                          className={`py-1 rounded text-xs font-medium ${
                            (recurRule.days || []).includes(day)
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
                          }`}
                        >
                          {day.charAt(0).toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">End Condition</label>
                  <div className="space-y-2 mt-1">
                    <div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          checked={!recurRule.count && !recurRule.until}
                          onChange={() => setRecurRule({ ...recurRule, count: undefined, until: undefined })}
                        />
                        <span>No end date</span>
                      </label>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          checked={!!recurRule.count}
                          onChange={() => setRecurRule({ ...recurRule, count: 8, until: undefined })}
                        />
                        <input
                          type="number"
                          min="1"
                          max="52"
                          value={recurRule.count || 8}
                          onChange={(e) => setRecurRule({ ...recurRule, count: parseInt(e.target.value), until: undefined })}
                          className="w-16 px-2 py-1 rounded border text-sm dark:bg-slate-700 dark:border-slate-600"
                        />
                        <span>classes</span>
                      </label>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          checked={!!recurRule.until}
                          onChange={() => setRecurRule({ ...recurRule, until: '2026-12-31', count: undefined })}
                        />
                        <input
                          type="date"
                          value={recurRule.until || '2026-12-31'}
                          onChange={(e) => setRecurRule({ ...recurRule, until: e.target.value, count: undefined })}
                          className="px-2 py-1 rounded border text-sm dark:bg-slate-700 dark:border-slate-600"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {recurrencePreview && (
                  <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded text-sm text-blue-700 dark:text-blue-300">
                    {recurrencePreview}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL: Scheduling */}
        <div className="space-y-4">
          {/* Timezone Info */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3">
            <div className="flex justify-between items-start gap-2">
              <div>
                <p className="text-xs text-slate-600 dark:text-slate-400">Tutor timezone: <span className="font-semibold">{homeTz}</span></p>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Your timezone: <span className="font-semibold">{parentTz}</span></p>
              </div>
              <button
                onClick={() => setEditingTz(true)}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                <Edit2 size={14} />
              </button>
            </div>
            {editingTz && (
              <div className="mt-2 space-y-2">
                <select
                  value={tzInput}
                  onChange={(e) => setTzInput(e.target.value)}
                  className="w-full px-2 py-1 rounded text-sm border dark:bg-slate-700 dark:border-slate-600"
                >
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setParentTz(tzInput); setEditingTz(false); setSelectedTime(null); setSelectedDate(null); }}
                    className="flex-1 px-2 py-1 bg-blue-600 text-white text-xs rounded"
                  >
                    Update
                  </button>
                  <button onClick={() => setEditingTz(false)} className="flex-1 px-2 py-1 bg-slate-300 text-xs rounded dark:bg-slate-600">Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* Time Slots */}
          {morningTimes.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Morning (5 AM - 12 PM)</h3>
              <div className="grid grid-cols-5 gap-2">
                {morningTimes.map((slot) => (
                  <button
                    key={slot.hour}
                    onClick={() => setSelectedTime(slot)}
                    className={`py-2 rounded text-xs font-medium ${
                      selectedTime?.hour === slot.hour
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {slot.displayTime}
                  </button>
                ))}
              </div>
            </div>
          )}

          {eveningTimes.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Evening (12 PM - 11 PM)</h3>
              <div className="grid grid-cols-5 gap-2">
                {eveningTimes.map((slot) => (
                  <button
                    key={slot.hour}
                    onClick={() => setSelectedTime(slot)}
                    className={`py-2 rounded text-xs font-medium ${
                      selectedTime?.hour === slot.hour
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {slot.displayTime}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Calendar */}
          <div>
            <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Select Date</h3>
            <div className="space-y-3">
              {twoMonths.map(({ year, month }) => {
                const key = getYearMonthKey(year, month);
                const daysInMonth = getDaysInMonth(year, month);
                const firstDay = getFirstDayOfMonth(year, month);
                const monthDates = datesByMonth[key] || [];
                const monthDateSet = new Set(monthDates);

                return (
                  <div key={key}>
                    <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 text-center">{formatMonthYear(year, month)}</p>
                    <div className="grid grid-cols-7 gap-1">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => <div key={d} className="text-center text-xs font-semibold text-slate-500">{d}</div>)}
                      {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
                      {Array.from({ length: daysInMonth }).map((_, i) => {
                        const day = i + 1;
                        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const isAvailable = monthDateSet.has(dateStr);
                        return (
                          <button
                            key={day}
                            disabled={!isAvailable}
                            onClick={() => setSelectedDate(dateStr)}
                            className={`py-1 rounded text-xs ${
                              !isAvailable
                                ? 'text-slate-300 cursor-not-allowed'
                                : selectedDate === dateStr
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300 hover:bg-slate-300'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Summary & Actions */}
      <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
        {selectedDate && selectedTime && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 rounded text-sm">
            <p className="text-slate-700 dark:text-slate-300">
              <span className="font-semibold">{classCount}</span> {classCount === 1 ? 'class' : 'classes'} scheduled
              {selectedDate && ` starting ${new Date(selectedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
            </p>
          </div>
        )}

        {error && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>}

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={() => void handleConfirm()} disabled={!selectedChild || !selectedTime || !selectedDate || submitting}>
            {submitting ? 'Booking…' : 'Confirm Booking'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
